import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Chip, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, IconButton, Paper, Alert, CircularProgress, Tooltip,
} from '@mui/material';
import {
  Visibility as EyeIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Download as DownloadIcon } from '@mui/icons-material';
import {
  GET_SALES_ENDPOINT, GET_DASHBOARD_STATS_ENDPOINT,
  PUT_SALES_ENDPOINT, EXPORT_SALES_ENDPOINT,
  GET_SALES_CHOICES_ENDPOINT,
} from "../../config/urls";
import DoorLocksFeatureLayout from './DoorLocksFeatureLayout';
import { SalesEditModal } from './EditModals';
import dayjs from 'dayjs';
import { formatWithTzLabel } from '../../utils/dateUtils';
import AppTable from '../../common/app-table';
import AppDatePicker from '../../common/app-date-picker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import TableSearchInput from '../TableSearchInput';
import AppDropdown from '../../common/app-dropdown';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { grayFontColor } from '../../cssUtils/constants';
import ButtonOne from '../../common/normal-button';
import ButtonTwo from '../../common/dashboard-button';

function getStatusColor(status) {
  const colors = {
    'New': 'primary', 'Proposal Sent': 'info', 'In Negotiation': 'warning',
    'Closed Won': 'success', 'Closed Lost': 'error', 'Cancelled': 'error',
  };
  return colors[status] || 'default';
}

export default function SalesTab({ activeTab = 2, counts = {}, onTabChange, onBack, onNavigate }) {
  const [salesData, setSalesData]     = useState([]);
  const [totalCount, setTotalCount]   = useState(0);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [editError, setEditError]     = useState(null);
  const [editSuccess, setEditSuccess] = useState(false);
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [stats, setStats]             = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [exporting, setExporting]     = useState(false);
  const [charCount, setCharCount]     = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter]   = useState('');
  const [lockTypeFilter, setLockTypeFilter] = useState('');
  const [statusFilter, setStatusFilter]     = useState('');
  const [updatedByFilter, setUpdatedByFilter]     = useState('');
  const [updatedDateFilter, setUpdatedDateFilter] = useState('');

  // Filter dropdown choices (city / lock type / status / updated_by), fetched from sales_choices
  const [filterChoices, setFilterChoices] = useState({ cities: [], lock_types: [], sales_statuses: [], updated_by: [] });

  // Modals
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [modalOpen, setModalOpen]           = useState(false);
  const [editModalOpen, setEditModalOpen]   = useState(false);

  const [tab, setTab] = useState(activeTab);

  const handleTabChange = onTabChange || ((e, newValue) => {
    setTab(newValue);
    if (onNavigate) {
      const tabKeyMap = ["installation", "service", "sales", "billing_dashboard"];
      newValue === 3 ? onNavigate("insights") : onNavigate(tabKeyMap[newValue] || "installation");
    }
  });
  const handleBack = onBack || (() => { onNavigate ? onNavigate("insights") : window.history.back(); });

  const [editFormData, setEditFormData] = useState({
    ticket_code: '', customer_phone: '', wa_number: '', customer_name: '', wa_name: '',
    door_type: '', quantity: 1, amount: '', sales_status: 'New',
    address: { flat: '', street: '', area: '', pincode: '', city: '', state: '', country: '' },
    features: { fingerprint_access: false, rfid_card_access: false, pin_code_access: false, mechanical_key_access: false, mobile_app_access: false, video_access: false },
  });

  const getToken = () => localStorage.getItem('token');

  const formatCurrency = (amount) => amount ? `₹${parseFloat(amount).toLocaleString('en-IN')}` : '₹0';

  // ── Data fetching ──────────────────────────────────────────────────────────

  useEffect(() => {
    setLoadingStats(true);
    fetch(GET_DASHBOARD_STATS_ENDPOINT, { headers: { 'X-Tenant-ID': 'dormakaba' }, method: 'GET' })
      .then(res => res.json())
      .then(data => setStats(data))
      .catch(() => setStats(null))
      .finally(() => setLoadingStats(false));
  }, []);

  const effectiveCounts = stats ? {
    installation: stats.total_installations, service: stats.total_services,
    sales: stats.total_sales, tickets: stats.total_tickets, ...counts,
  } : { installation: '-', service: '-', sales: '-', tickets: '-', ...counts };

  // ── Filter choices (city / lock type / status) ──────────────────────────────

  useEffect(() => {
    const token = getToken();
    fetch(GET_SALES_CHOICES_ENDPOINT, {
      headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': localStorage.getItem('tenant') },
      method: 'GET',
    })
      .then(res => res.json())
      .then(data => setFilterChoices({
        cities: data.cities || [],
        lock_types: data.lock_types || [],
        sales_statuses: data.sales_statuses || [],
        updated_by: data.updated_by || [],
      }))
      .catch(() => setFilterChoices({ cities: [], lock_types: [], sales_statuses: [], updated_by: [] }));
  }, []);

  const cityOptions = React.useMemo(() => [
    { label: "All Cities", value: "" },
    ...filterChoices.cities.map((c) => ({ label: c.city_name, value: c.city_name })),
  ], [filterChoices.cities]);

  const lockTypeOptions = React.useMemo(() => [
    { label: "All Lock Types", value: "" },
    ...filterChoices.lock_types.map((l) => ({ label: l.lock_name, value: l.lock_name })),
  ], [filterChoices.lock_types]);

  const statusOptions = React.useMemo(() => [
    { label: "All Statuses", value: "" },
    ...filterChoices.sales_statuses.map((s) => ({ label: s, value: s })),
  ], [filterChoices.sales_statuses]);

  const updatedByOptions = React.useMemo(() => [
    { label: "All Updated By", value: "" },
    ...filterChoices.updated_by.map((n) => ({ label: n, value: n })),
  ], [filterChoices.updated_by]);

  const fetchSalesData = async () => {
    setLoading(true); setError(null);
    const token = getToken();
    const params = new URLSearchParams();
    if (searchQuery) params.append('search', searchQuery);
    if (cityFilter)  params.append('city', cityFilter);
    if (lockTypeFilter) params.append('lock_type', lockTypeFilter);
    if (statusFilter)   params.append('status', statusFilter);
    if (updatedByFilter)   params.append('updated_by', updatedByFilter);
    if (updatedDateFilter) params.append('updated_date', updatedDateFilter);
    let endpoint = `${GET_SALES_ENDPOINT}?limit=${rowsPerPage}&offset=${page * rowsPerPage}`;
    if (params.toString()) endpoint += `&${params.toString()}`;

    try {
      const response = await fetch(endpoint, {
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json", "X-Tenant-Id": localStorage.getItem('tenant') },
      });
      if (!response.ok) throw new Error('Failed to fetch sales data');
      const jsonData = await response.json();
      setSalesData(jsonData.results || []);
      setTotalCount(jsonData.count || 0);
    } catch (error) {
      console.error('Error fetching sales data:', error.message);
      setError('Failed to load sales. Please try again.');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchSalesData(); }, [page, rowsPerPage, searchQuery, cityFilter, lockTypeFilter, statusFilter, updatedByFilter, updatedDateFilter]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleSearch = (e) => {
    const value = e.target.value;
    if (value.length > 50) return;
    setSearchQuery(value);
    setCharCount(value.length);
    setPage(0);
  };

  const handleChangePage        = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

  const handleEdit = (record) => {
    setEditFormData({
      sales_id: record.sales_id, ticket_code: record.ticket_code,
      customer_phone: record.customer_phone, wa_number: record.wa_number,
      customer_name: record.customer_name, wa_name: record.wa_name,
      door_type: record.door_type, quantity: record.quantity,
      amount: record.amount, sales_status: record.sales_status,
      address: { ...record.address_details },
      features: {
        fingerprint_access: record.fingerprint_access, rfid_card_access: record.rfid_card_access,
        pin_code_access: record.pin_code_access, mechanical_key_access: record.mechanical_key_access,
        mobile_app_access: record.mobile_app_access, video_access: record.video_access,
      },
    });
    setEditModalOpen(true);
  };

  const handleUpdate = async () => {
    const token = getToken();
    try {
      const response = await fetch(`${PUT_SALES_ENDPOINT}${editFormData.sales_id}/`, {
        method: 'PUT',
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json", "X-Tenant-Id": localStorage.getItem('tenant') },
        body: JSON.stringify(editFormData),
      });
      if (response.ok) { setEditError(null); setEditSuccess(true); }
      else {
        const data = await response.json();
        const msgs = [];
        if (data?.ticket?.length)           msgs.push(`Ticket Error: ${data.ticket.join(' ')}`);
        if (data?.address?.length)          msgs.push(`Address Error: ${data.address.join(' ')}`);
        if (data?.customer_phone?.length)   msgs.push(`Phone Error: ${data.customer_phone.join(' ')}`);
        if (data?.customer_name?.length)    msgs.push(`Name Error: ${data.customer_name.join(' ')}`);
        if (data?.issue_type?.length)       msgs.push(`Issue Type Error: ${data.issue_type.join(' ')}`);
        if (data?.service_status?.length)   msgs.push(`Service Status Error: ${data.service_status.join(' ')}`);
        if (data?.issue_description?.length) msgs.push(`Issue Description Error: ${data.issue_description.join(' ')}`);
        setEditError(msgs.length ? msgs.join(' | ') : 'Failed to update. Please try again.');
      }
      fetchSalesData();
    } catch (error) {
      console.error('Error updating sales record:', error.message);
      setEditError('Failed to update service. Please try again.');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    const token = getToken();
    const params = new URLSearchParams();
    if (searchQuery)    params.append('search', searchQuery);
    if (cityFilter)     params.append('city', cityFilter);
    if (lockTypeFilter) params.append('lock_type', lockTypeFilter);
    if (statusFilter)   params.append('status', statusFilter);
    if (updatedByFilter)   params.append('updated_by', updatedByFilter);
    if (updatedDateFilter) params.append('updated_date', updatedDateFilter);
    let exportUrl = EXPORT_SALES_ENDPOINT;
    if (params.toString()) exportUrl += `?${params.toString()}`;
    try {
      const response = await fetch(exportUrl, { headers: { "Authorization": `Bearer ${token}`, "X-Tenant-Id": localStorage.getItem('tenant') } });
      if (!response.ok) throw new Error('Failed to export data');
      const blob = await response.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = 'sales.csv';
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error.message);
      setError('Failed to export data. Please try again.');
    } finally { setExporting(false); }
  };

  // ── Column definitions ─────────────────────────────────────────────────────

  const handleView = useCallback((item) => { setSelectedRecord(item); setModalOpen(true); }, []);
  const handleEditCb = useCallback((item) => handleEdit(item), []);

  const tableColumns = React.useMemo(() => [
    { id: "ticket_code",    label: "Ticket ID"       },
    { id: "customer_name",  label: "Customer Name"   },
    { id: "customer_phone", label: "Customer Phone"  },
    { id: "wa_name",        label: "WA Name"         },
    { id: "wa_number",      label: "WA Phone"        },
    { id: "city",           label: "City",           render: (item) => item.address_details?.city },
    {
      id: "sales_status",
      label: "Status",
      render: (item) => <Chip label={item.sales_status} color={getStatusColor(item.sales_status)} size="small" />,
    },
    {
      id: "updated_at",
      label: "Updated At",
      render: (item) => formatWithTzLabel(item.updated_at, "DD/MM/YYYY, hh:mm A"),
    },
    { id: "updated_by", label: "Updated By" },
    {
      id: "__actions__",
      label: "Actions",
      render: (item) => (
        <Box display="flex" gap={1}>
          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleView(item); }}>
            <EyeIcon />
          </IconButton>
          <IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); handleEditCb(item); }}>
            <EditIcon />
          </IconButton>
        </Box>
      ),
    },
  ], [handleView, handleEditCb]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const FeatureRow = ({ label, value }) => (
    <Grid item xs={6}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2">{label}:</Typography>
        <Chip label={value ? 'Yes' : 'No'} color={value ? 'success' : 'error'} size="small" />
      </Box>
    </Grid>
  );

  return (
    <DoorLocksFeatureLayout
      activeTab={tab} onTabChange={handleTabChange} onBack={handleBack}
      counts={effectiveCounts} loadingStats={loadingStats}
    >
      {loadingStats ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Filter bar */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
            <Box display="flex" alignItems="center" sx={{ width: "100%", minWidth: 0, gap: 1, flexWrap: "wrap" }}>
              <Box sx={{ flex: "1 1 280px", minWidth: 220 }}>
                <TableSearchInput
                  searchQuery={searchQuery}
                  onSearchChange={handleSearch}
                  tooltip="Search by Ticket ID, Customer Phone, Customer Name, City"
                  placeholder="Search by Ticket ID, Customer Phone, Customer Name, City"
                  charCount={charCount}
                />
              </Box>

              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />}
                selectedValue={lockTypeFilter}
                onMenuSelect={(value) => { setLockTypeFilter(value); setPage(0); }}
                menuItems={lockTypeOptions}
                sx={{ minWidth: 140, whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {lockTypeOptions.find(o => o.value === lockTypeFilter)?.label ?? "All Lock Types"}
              </AppDropdown>

              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />}
                selectedValue={cityFilter}
                onMenuSelect={(value) => { setCityFilter(value); setPage(0); }}
                menuItems={cityOptions}
                sx={{ minWidth: 130, whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {cityOptions.find(o => o.value === cityFilter)?.label ?? "All Cities"}
              </AppDropdown>

              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />}
                selectedValue={statusFilter}
                onMenuSelect={(value) => { setStatusFilter(value); setPage(0); }}
                menuItems={statusOptions}
                sx={{ minWidth: 130, whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {statusOptions.find(o => o.value === statusFilter)?.label ?? "All Statuses"}
              </AppDropdown>

              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />}
                selectedValue={updatedByFilter}
                onMenuSelect={(value) => { setUpdatedByFilter(value); setPage(0); }}
                menuItems={updatedByOptions}
                sx={{ minWidth: 150, whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {updatedByOptions.find(o => o.value === updatedByFilter)?.label ?? "All Updated By"}
              </AppDropdown>

              <Box sx={{ width: 175, flexShrink: 0 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <AppDatePicker
                    label="Updated Date"
                    fullWidth
                    value={updatedDateFilter ? dayjs(updatedDateFilter) : null}
                    onChange={(newValue) => { setUpdatedDateFilter(newValue ? newValue.format('YYYY-MM-DD') : ''); setPage(0); }}
                  />
                </LocalizationProvider>
              </Box>

              <ButtonOne
                variant="contained"
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                onClick={() => { setSearchQuery(''); setCityFilter(''); setLockTypeFilter(''); setStatusFilter(''); setUpdatedByFilter(''); setUpdatedDateFilter(''); setCharCount(0); setPage(0); }}
              >
                Reset
              </ButtonOne>

              <ButtonTwo
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleExport}
                disabled={loading || exporting}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {exporting ? 'Exporting...' : 'Export CSV'}
              </ButtonTwo>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          {/* Table */}
          <AppTable
            columns={tableColumns}
            rows={salesData}
            rowKey="sales_id"
            loading={loading}
            emptyMessage="No sales found"
            maxHeight={440}
            headCellSx={{ backgroundColor: "#f5f5f5" }}
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />

          {/* View Details Modal */}
          <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Sales Details</DialogTitle>
            <DialogContent dividers>
              {selectedRecord && (
                <Grid container spacing={2}>
                  {[
                    ["Ticket ID",       selectedRecord.ticket_code],
                    ["Customer Phone",  selectedRecord.customer_phone],
                    ["Customer Name",   selectedRecord.customer_name],
                    ["WA Phone",        selectedRecord.wa_number],
                    ["WA Name",         selectedRecord.wa_name],
                    ["City",            selectedRecord.address_details?.city],
                    ["Status",          selectedRecord.sales_status],
                    ["Email",           selectedRecord.customer_email],
                    ["Door Type",       selectedRecord.door_type],
                    ["Quantity",        selectedRecord.quantity],
                  ].map(([label, value]) => (
                    <Grid item xs={12} sm={6} key={label}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>{label}</Typography>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="body2">{value}</Typography>
                      </Paper>
                    </Grid>
                  ))}

                  {selectedRecord.address_details && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Address</Typography>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="body2">
                          {selectedRecord.address_details.flat}, {selectedRecord.address_details.street}<br />
                          {selectedRecord.address_details.area}<br />
                          {selectedRecord.address_details.city}, {selectedRecord.address_details.state}<br />
                          PIN: {selectedRecord.address_details.pincode}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Features</Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        <FeatureRow label="Fingerprint Access"   value={selectedRecord.fingerprint_access}   />
                        <FeatureRow label="RFID Card Access"     value={selectedRecord.rfid_card_access}     />
                        <FeatureRow label="Pin Code Access"      value={selectedRecord.pin_code_access}      />
                        <FeatureRow label="Mechanical Key Access" value={selectedRecord.mechanical_key_access} />
                        <FeatureRow label="Mobile App Access"    value={selectedRecord.mobile_app_access}    />
                        <FeatureRow label="Video Access"         value={selectedRecord.video_access}         />
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setModalOpen(false)} color="primary">Close</Button>
            </DialogActions>
          </Dialog>

          {/* Edit Modal */}
          <SalesEditModal
            open={editModalOpen}
            onClose={() => { setEditModalOpen(false); setEditError(null); setEditSuccess(false); }}
            formData={editFormData}
            onInputChange={(field, value) => setEditFormData(prev => ({ ...prev, [field]: value }))}
            onAddressChange={(field, value) => setEditFormData(prev => ({ ...prev, address: { ...prev.address, [field]: value } }))}
            onFeatureChange={(feature, value) => setEditFormData(prev => ({ ...prev, features: { ...prev.features, [feature]: value } }))}
            onUpdate={handleUpdate}
            error={editError}
            success={editSuccess}
          />
        </>
      )}
    </DoorLocksFeatureLayout>
  );
}